import { useCallback, useEffect, useMemo, useState } from 'react';
import { countCustomerAccounts, fetchCustomerAccounts } from '../../infra/customerRepository'
import type { CustomerAccountListQuery } from '../../domain/customerTypes';
import { usePaginatedRemoteList } from '../../../shared/data/usePaginatedRemoteList';
import { findCustomerIdsByCheckText } from '../../../cash/infra-api';
import { subscribeCustomerSynced } from '../../../sync/core/customerSyncListeners';
import { useEffectiveBusinessCapabilities } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

const CUSTOMER_CUSTOMER_PAGE_SIZE_STORAGE_KEY = 'fiado_customer_page_size';

export type CustomerListPageSize = 5 | 10;

export type CustomerStatusFilter = 'all' | 'has_debt' | 'no_debt' | 'blocked' | 'overdue';

export type CustomerUtilizationFilter = 'all' | 'unused' | 'near_limit' | 'over_limit';

export type CustomerDatePreset = 'all' | 'today' | 'this_week' | 'this_month';

export type CustomerSortBy =
  | 'name_asc'
  | 'name_desc'
  | 'balance_desc'
  | 'balance_asc'
  | 'updated_desc'
  | 'limit_desc'
  | 'limit_asc'
  | 'utilization_desc'
  | 'utilization_asc';

export const STATUS_FILTER_OPTIONS: Array<{ value: CustomerStatusFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'has_debt', label: 'Con deuda' },
  { value: 'no_debt', label: 'Sin deuda' },
  { value: 'blocked', label: 'Bloqueados' },
  { value: 'overdue', label: 'Vencidos' },
];

export const UTILIZATION_FILTER_OPTIONS: Array<{ value: CustomerUtilizationFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'unused', label: 'Sin usar' },
  { value: 'near_limit', label: 'Cerca del límite (>80%)' },
  { value: 'over_limit', label: 'Sobre el límite' },
];

export const DATE_PRESET_OPTIONS: Array<{ value: CustomerDatePreset; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'today', label: 'Hoy' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'this_month', label: 'Este mes' },
];

export const SORT_OPTIONS: Array<{ value: CustomerSortBy; label: string }> = [
  { value: 'name_asc', label: 'Nombre (A-Z)' },
  { value: 'name_desc', label: 'Nombre (Z-A)' },
  { value: 'balance_desc', label: 'Saldo (mayor deuda)' },
  { value: 'balance_asc', label: 'Saldo (menor deuda)' },
  { value: 'updated_desc', label: 'Última actividad' },
  { value: 'limit_desc', label: 'Límite (mayor)' },
  { value: 'limit_asc', label: 'Límite (menor)' },
  { value: 'utilization_desc', label: 'Uso (mayor)' },
  { value: 'utilization_asc', label: 'Uso (menor)' },
];

type Params = {
  searchQuery?: string;
  statusFilter?: CustomerStatusFilter;
  sortBy?: CustomerSortBy;
  utilizationFilter?: CustomerUtilizationFilter;
  datePreset?: CustomerDatePreset;
};

export const useCustomerListFlow = ({
  searchQuery = '',
  statusFilter = 'all',
  sortBy = 'updated_desc',
  utilizationFilter = 'all',
  datePreset = 'all',
}: Params = {}) => {
  // "Tengo el papel en la mano": el search también localiza al cliente por
  // número/banco/librador del cheque (lookup async en pos.checks).
  const checksEnabled = useEffectiveBusinessCapabilities().has('checks');
  const [checkMatchIds, setCheckMatchIds] = useState<string[]>([]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!checksEnabled || q.length < 2) {
      setCheckMatchIds([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void findCustomerIdsByCheckText(q)
        .then((ids) => { if (!cancelled) setCheckMatchIds(ids); })
        .catch(() => { if (!cancelled) setCheckMatchIds([]); });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, checksEnabled]);

  const listQuery = useMemo<CustomerAccountListQuery>(
    () => ({
      search: searchQuery,
      searchExtraCustomerIds: checkMatchIds.length > 0 ? checkMatchIds : undefined,
      statusFilter,
      sortBy,
      utilizationFilter,
      datePreset,
    }),
    [searchQuery, checkMatchIds, statusFilter, sortBy, utilizationFilter, datePreset]
  );

  // checkMatchIds va en la criteriaKey: el lookup es async y el pager solo
  // recarga cuando cambia la key — sin esto los extra-ids llegan tarde y no
  // gatillan el refetch que los aplica.
  const criteriaKey = useMemo(
    () => `${searchQuery}|${checkMatchIds.join(',')}|${statusFilter}|${sortBy}|${utilizationFilter}|${datePreset}`,
    [searchQuery, checkMatchIds, statusFilter, sortBy, utilizationFilter, datePreset]
  );

  const fetchCustomerPage = useCallback(
    ({ limit, offset, query }: { limit: number; offset: number; query: CustomerAccountListQuery }) =>
      fetchCustomerAccounts({ limit, offset, query }),
    []
  );

  const paging = usePaginatedRemoteList({
    storageKey: CUSTOMER_CUSTOMER_PAGE_SIZE_STORAGE_KEY,
    criteriaKey,
    query: listQuery,
    logLabel: 'Clientes',
    fetchPage: fetchCustomerPage,
    count: countCustomerAccounts,
  });

  // Cuando un pull trae cambios (venta a cuenta desde POS, cobro, void),
  // recargar la página actual — sin esto la card queda con el saldo viejo
  // aunque el snapshot ya esté fresco en RAM (los KPIs sí se suscriben).
  useEffect(() => {
    return subscribeCustomerSynced(() => {
      void paging.load();
    });
  }, [paging.load]);

  return {
    customers: paging.items,
    customerPageIndex: paging.pageIndex,
    customerPageSize: paging.pageSize,
    setCustomerPageSize: paging.setPageSize,
    customerHasMore: paging.hasMore,
    customerLoading: paging.loading,
    customerError: paging.error,
    customerResultTotalCount: paging.totalCount,
    loadCustomers: paging.load,
    loadCustomerNextPage: paging.loadNextPage,
    loadCustomerPreviousPage: paging.loadPreviousPage,
    refreshCustomerTotalCount: paging.refreshTotalCount,
    customerListRef: paging.flatListRef,
    customerWideScrollRef: paging.scrollViewRef,
  };
};
